import { MouseEvent, useCallback } from 'react';

import { SelectionMode } from './types';

export interface UseToggleButtonGroup {
  value: string[];
  onChange: (value: string[]) => void;
  /** @default SelectionMode.SINGLE */
  selectionMode?: SelectionMode;
  /** @default false */
  disabled?: boolean;
}

const useToggleButtonGroup = ({
  value,
  onChange,
  selectionMode = SelectionMode.SINGLE,
  disabled = false,
}: UseToggleButtonGroup) => {
  const getIsSelected = useCallback((itemValue: string) => value.includes(itemValue), [value]);

  const toggleItem = useCallback(
    (itemValue: string) => (_e: MouseEvent<HTMLElement>) => {
      if (disabled) return;

      const isSelected = getIsSelected(itemValue);

      if (isSelected) {
        // In SINGLE mode, enforce at least one selected — no-op
        if (selectionMode === SelectionMode.SINGLE) return;
        // In MULTI mode, allow deselect freely
        onChange(value.filter((v) => v !== itemValue));
      } else {
        onChange(selectionMode === SelectionMode.SINGLE ? [itemValue] : [...value, itemValue]);
      }
    },
    [disabled, getIsSelected, onChange, selectionMode, value]
  );

  return { getIsSelected, toggleItem };
};

export default useToggleButtonGroup;
